/**
 * Painel Streamer — Central do criador de conteúdo (player-side).
 *
 * Vibe: dashboard de criador estilo Twitch — roxo (#9146FF) + neon
 * (magenta/ciano), fundos pretos/muito escuros, containers quadrados
 * (rounded-md no máximo), bordas/brilhos neon sutis.
 *
 * Seções (abas): Visão Geral, Minha Conta, Cupons, Comissões, Sessões Live,
 * Como Apareço.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Tv, Radio, LayoutDashboard, UserCog, Ticket, Wallet, Video, Eye,
  Loader2, Copy, Check, Download, Plus, Trash2, ShieldCheck, ExternalLink,
  Play, Square, Clock, TrendingUp, DollarSign, CheckCircle2, Star, Link2,
} from 'lucide-react';
import { useToast } from '@/shared/contexts/ToastContext';
import {
  getStreamerMe, updateStreamerMe,
  getSocials, addSocial, deleteSocial,
  getConnections, addConnection, deleteConnection,
  getMyCoupons, generateFixedCoupon, getFixedHistory,
  getMyCommissions, getMySessions, startLive, stopLive,
  type StreamerMe, type StreamerSocial, type StreamerConnection,
  type StreamerCoupon, type CommissionsView, type StreamerSession,
} from '@/shared/services/streamerService';

/* ─────────────────────────── Helpers ─────────────────────────── */

const PURPLE = '#9146FF';

function formatBRL(v?: number | null) {
  return (v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}
function formatDate(iso?: string | null) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
}
function formatDateTime(iso?: string | null) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) + ' ' +
         d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

/** Detecta a plataforma de uma rede social pelo domínio da URL. */
function detectPlatform(url: string): string {
  const u = url.toLowerCase();
  if (u.includes('instagram.')) return 'instagram';
  if (u.includes('tiktok.')) return 'tiktok';
  if (u.includes('twitch.')) return 'twitch';
  if (u.includes('kick.')) return 'kick';
  if (u.includes('discord.') || u.includes('discord.gg')) return 'discord';
  if (u.includes('facebook.') || u.includes('fb.com')) return 'facebook';
  if (u.includes('youtube.') || u.includes('youtu.be')) return 'youtube';
  if (u.includes('twitter.') || u.includes('x.com')) return 'x';
  return 'link';
}

const PLATFORM_LABEL: Record<string, string> = {
  twitch: 'Twitch', youtube: 'YouTube', kick: 'Kick', tiktok: 'TikTok',
  instagram: 'Instagram', x: 'X (Twitter)', twitter: 'X (Twitter)',
  discord: 'Discord', facebook: 'Facebook', link: 'Link',
};

const CONNECTION_PLATFORMS = ['twitch', 'youtube', 'kick', 'tiktok'] as const;

/* Constrói a URL do player embutido a partir da conexão primária + hostname. */
function buildEmbedUrl(conn: StreamerConnection): string | null {
  const host = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
  const p = conn.platform.toLowerCase();
  if (p === 'twitch') {
    return `https://player.twitch.tv/?channel=${encodeURIComponent(conn.channelId)}&parent=${host}&muted=true`;
  }
  if (p === 'youtube') {
    return `https://www.youtube.com/embed/live_stream?channel=${encodeURIComponent(conn.channelId)}`;
  }
  if (p === 'kick') {
    return `https://player.kick.com/${encodeURIComponent(conn.channelId)}`;
  }
  return null;
}

/* ─────────────────────────── Tabs ─────────────────────────── */

type TabId = 'overview' | 'account' | 'coupons' | 'commissions' | 'sessions' | 'appearance';

const TABS: { id: TabId; label: string; icon: React.ReactNode }[] = [
  { id: 'overview', label: 'Visão Geral', icon: <LayoutDashboard size={16} /> },
  { id: 'account', label: 'Minha Conta', icon: <UserCog size={16} /> },
  { id: 'coupons', label: 'Cupons', icon: <Ticket size={16} /> },
  { id: 'commissions', label: 'Comissões', icon: <Wallet size={16} /> },
  { id: 'sessions', label: 'Sessões Live', icon: <Video size={16} /> },
  { id: 'appearance', label: 'Como Apareço', icon: <Eye size={16} /> },
];

/* Card base — container quadrado, fundo preto, borda neon roxa. */
function Panel({ children, className = '', glow = false }: {
  children: React.ReactNode; className?: string; glow?: boolean;
}) {
  return (
    <div
      className={`rounded-md ${className}`}
      style={{
        background: 'linear-gradient(180deg, #0d0714 0%, #060309 100%)',
        border: `1px solid ${PURPLE}33`,
        boxShadow: glow
          ? `0 0 24px rgba(145,70,255,0.18), inset 0 1px 0 rgba(255,255,255,0.03)`
          : `0 4px 20px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.03)`,
      }}
    >
      {children}
    </div>
  );
}

/* ═══════════════════════════ Página ═══════════════════════════ */

export default function StreamerCentralPage() {
  const { notify } = useToast();
  const [tab, setTab] = useState<TabId>('overview');

  const [me, setMe] = useState<StreamerMe | null>(null);
  const [loading, setLoading] = useState(true);
  const [notStreamer, setNotStreamer] = useState(false);

  const reload = useCallback(async () => {
    try {
      const data = await getStreamerMe();
      setMe(data);
      setNotStreamer(false);
    } catch {
      setNotStreamer(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 size={30} className="animate-spin" style={{ color: PURPLE }} />
      </div>
    );
  }

  if (notStreamer || !me) {
    return (
      <Panel className="p-10 text-center max-w-lg mx-auto" glow>
        <div className="w-14 h-14 mx-auto rounded-md flex items-center justify-center mb-4"
             style={{ background: `${PURPLE}22`, border: `1px solid ${PURPLE}55` }}>
          <Tv size={26} className="text-[#c8a6ff]" />
        </div>
        <h2 className="text-xl font-black text-white">Você ainda não é um streamer oficial</h2>
        <p className="text-sm text-white/60 mt-2">
          O Painel Streamer é exclusivo para criadores de conteúdo parceiros da Liberty.
          Fale com a equipe para se tornar um streamer oficial.
        </p>
      </Panel>
    );
  }

  return (
    <div>
      {/* Cabeçalho neon */}
      <div className="relative overflow-hidden rounded-md mb-6 p-6"
           style={{
             background: 'linear-gradient(120deg, #120726 0%, #06030b 60%)',
             border: `1px solid ${PURPLE}44`,
             boxShadow: `0 0 30px rgba(145,70,255,0.18)`,
           }}>
        <div className="absolute -top-16 -right-10 w-64 h-64 rounded-full pointer-events-none"
             style={{ background: 'radial-gradient(circle, rgba(145,70,255,0.25), transparent 70%)' }} />
        <div className="absolute -bottom-20 left-10 w-56 h-56 rounded-full pointer-events-none"
             style={{ background: 'radial-gradient(circle, rgba(79,120,168,0.12), transparent 70%)' }} />
        <div className="relative flex items-center gap-4">
          <div className="w-16 h-16 rounded-md overflow-hidden shrink-0 border-2"
               style={{ borderColor: `${PURPLE}88`, boxShadow: `0 0 18px rgba(145,70,255,0.4)` }}>
            {me.avatar
              ? <img src={me.avatar} alt={me.nick} className="w-full h-full object-cover" />
              : <div className="w-full h-full flex items-center justify-center bg-black/50">
                  <Tv size={26} className="text-[#c8a6ff]" />
                </div>}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl md:text-3xl font-black text-white tracking-tight truncate">
                {me.nick}
              </h1>
              <StatusPill status={me.status} live={!!me.activeSession} />
            </div>
            <p className="text-sm text-white/55 mt-0.5">
              Painel Streamer · comissão de <span className="text-[#c8a6ff] font-bold">{me.commissionPct}%</span> ·
              desde {formatDate(me.createdAt)}
            </p>
          </div>
        </div>
      </div>

      {/* Abas */}
      <div className="flex gap-2 overflow-x-auto pb-2 mb-6 no-scrollbar">
        {TABS.map(t => {
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className="shrink-0 flex items-center gap-2 px-4 py-2 rounded-md text-sm font-bold transition-all cursor-pointer"
              style={active ? {
                background: `${PURPLE}22`,
                color: '#e5d4ff',
                border: `1px solid ${PURPLE}`,
                boxShadow: `0 0 14px rgba(145,70,255,0.35)`,
              } : {
                background: 'rgba(255,255,255,0.03)',
                color: 'rgba(255,255,255,0.6)',
                border: '1px solid rgba(255,255,255,0.07)',
              }}
            >
              {t.icon}{t.label}
            </button>
          );
        })}
      </div>

      {tab === 'overview' && <OverviewTab me={me} />}
      {tab === 'account' && <AccountTab me={me} onChanged={reload} notify={notify} />}
      {tab === 'coupons' && <CouponsTab me={me} onChanged={reload} notify={notify} />}
      {tab === 'commissions' && <CommissionsTab commissionPct={me.commissionPct} />}
      {tab === 'sessions' && <SessionsTab me={me} onChanged={reload} notify={notify} />}
      {tab === 'appearance' && <AppearanceTab me={me} />}

      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
}

/* ─────────────────────────── Status pill ─────────────────────────── */

function StatusPill({ status, live }: { status: string; live: boolean }) {
  if (live) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-black uppercase tracking-wider"
            style={{ background: 'rgba(239,68,68,0.15)', color: '#fca5a5', border: '1px solid rgba(239,68,68,0.5)' }}>
        <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> AO VIVO
      </span>
    );
  }
  const ok = status === 'ativo' || status === 'active';
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-black uppercase tracking-wider"
          style={ok
            ? { background: `${PURPLE}1e`, color: '#c8a6ff', border: `1px solid ${PURPLE}66` }
            : { background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.12)' }}>
      {status || '—'}
    </span>
  );
}

/* ═══════════════════════════ Visão Geral ═══════════════════════════ */

function MetricCard({ icon, label, value, accent }: {
  icon: React.ReactNode; label: string; value: string; accent: string;
}) {
  return (
    <Panel className="p-5">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-md flex items-center justify-center"
             style={{ background: `${accent}1e`, border: `1px solid ${accent}55`, color: accent }}>
          {icon}
        </div>
        <p className="text-[11px] font-extrabold uppercase tracking-[0.15em] text-white/50">{label}</p>
      </div>
      <p className="text-3xl font-black text-white tracking-tight" style={{ textShadow: `0 0 14px ${accent}44` }}>
        {value}
      </p>
    </Panel>
  );
}

function OverviewTab({ me }: { me: StreamerMe }) {
  const m = me.metrics;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
      <Panel className="p-5">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-md flex items-center justify-center"
               style={{ background: `${PURPLE}1e`, border: `1px solid ${PURPLE}55`, color: '#c8a6ff' }}>
            <Radio size={18} />
          </div>
          <p className="text-[11px] font-extrabold uppercase tracking-[0.15em] text-white/50">Status</p>
        </div>
        <div className="text-2xl font-black text-white">
          <StatusPill status={me.status} live={!!me.activeSession} />
        </div>
      </Panel>
      <MetricCard
        icon={<TrendingUp size={18} />}
        label="Recargas c/ meu cupom (mês)"
        value={formatBRL(m.recargasMesBrl)}
        accent="#8fb9de"
      />
      <MetricCard
        icon={<DollarSign size={18} />}
        label="Minha comissão gerada"
        value={formatBRL(m.comissaoGeradaBrl)}
        accent={PURPLE}
      />
      <MetricCard
        icon={<CheckCircle2 size={18} />}
        label="Sessões verificadas"
        value={String(m.sessoesVerificadas ?? 0)}
        accent="#f472b6"
      />
    </div>
  );
}

/* ═══════════════════════════ Minha Conta ═══════════════════════════ */

function AccountTab({ me, onChanged, notify }: {
  me: StreamerMe; onChanged: () => void; notify: (t: any, m: string) => void;
}) {
  const [nick, setNick] = useState(me.nick);
  const [bio, setBio] = useState(me.bio ?? '');
  const [avatar, setAvatar] = useState(me.avatar ?? '');
  const [saving, setSaving] = useState(false);

  const saveProfile = async () => {
    setSaving(true);
    try {
      await updateStreamerMe({ nick: nick.trim(), bio: bio.trim(), avatar: avatar.trim() });
      notify('success', 'Perfil atualizado!');
      onChanged();
    } catch (e: any) {
      notify('error', e?.message || 'Erro ao salvar perfil');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Editar perfil */}
      <Panel className="p-6">
        <h3 className="text-sm font-black uppercase tracking-wider text-[#c8a6ff] mb-4 flex items-center gap-2">
          <UserCog size={16} /> Perfil do criador
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Nick de exibição">
            <input value={nick} onChange={e => setNick(e.target.value)} maxLength={32}
              className={inputCls} placeholder="Seu nick de streamer" />
          </Field>
          <Field label="Avatar (URL da imagem)">
            <input value={avatar} onChange={e => setAvatar(e.target.value)}
              className={inputCls} placeholder="https://..." />
          </Field>
          <div className="md:col-span-2">
            <Field label="Bio">
              <textarea value={bio} onChange={e => setBio(e.target.value)} rows={3} maxLength={280}
                className={inputCls + ' resize-none'} placeholder="Fale um pouco sobre seu canal..." />
            </Field>
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <button onClick={saveProfile} disabled={saving}
            className="px-5 py-2 rounded-md text-sm font-bold cursor-pointer disabled:opacity-50 transition"
            style={{ background: PURPLE, color: '#fff', boxShadow: `0 0 16px rgba(145,70,255,0.4)` }}>
            {saving ? <Loader2 size={15} className="animate-spin inline" /> : 'Salvar perfil'}
          </button>
        </div>
      </Panel>

      <ConnectionsCard notify={notify} initial={me.connections} />
      <SocialsCard notify={notify} initial={me.socials} />
    </div>
  );
}

const inputCls =
  'w-full px-3 py-2 rounded-md bg-black/50 border border-[#9146FF]/25 text-white text-sm ' +
  'placeholder:text-white/25 focus:outline-none focus:border-[#9146FF] focus:ring-1 focus:ring-[#9146FF]/50 transition';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-bold uppercase tracking-wider text-white/45 mb-1.5">{label}</span>
      {children}
    </label>
  );
}

/* ── Conexões (canais oficiais) ── */
function ConnectionsCard({ initial, notify }: {
  initial: StreamerConnection[]; notify: (t: any, m: string) => void;
}) {
  const [conns, setConns] = useState<StreamerConnection[]>(initial);
  const [platform, setPlatform] = useState<string>('twitch');
  const [channelId, setChannelId] = useState('');
  const [channelUrl, setChannelUrl] = useState('');
  const [busy, setBusy] = useState(false);

  const refresh = () => getConnections().then(setConns).catch(() => {});

  const add = async () => {
    if (!channelId.trim() || !channelUrl.trim()) {
      notify('error', 'Informe o ID do canal e a URL.');
      return;
    }
    setBusy(true);
    try {
      await addConnection(platform, channelId.trim(), channelUrl.trim());
      notify('success', 'Canal adicionado! Aguarde a verificação.');
      setChannelId(''); setChannelUrl('');
      refresh();
    } catch (e: any) {
      notify('error', e?.message || 'Erro ao adicionar canal');
    } finally { setBusy(false); }
  };

  const remove = async (id: number) => {
    try { await deleteConnection(id); refresh(); notify('success', 'Canal removido.'); }
    catch (e: any) { notify('error', e?.message || 'Erro ao remover'); }
  };

  return (
    <Panel className="p-6">
      <h3 className="text-sm font-black uppercase tracking-wider text-[#c8a6ff] mb-1 flex items-center gap-2">
        <Video size={16} /> Canais oficiais
      </h3>
      <p className="text-[12px] text-white/50 mb-4">
        Conecte Twitch, YouTube, Kick ou TikTok. Canais verificados exibem o selo abaixo.
      </p>

      <div className="space-y-2 mb-4">
        {conns.length === 0 && (
          <p className="text-sm text-white/40 text-center py-4">Nenhum canal conectado ainda.</p>
        )}
        {conns.map(c => (
          <div key={c.id} className="flex items-center gap-3 px-3 py-2.5 rounded-md bg-white/[0.03] border border-white/[0.06]">
            <span className="text-xs font-black uppercase tracking-wide px-2 py-1 rounded"
                  style={{ background: `${PURPLE}1e`, color: '#c8a6ff' }}>
              {PLATFORM_LABEL[c.platform] ?? c.platform}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm text-white truncate font-semibold">{c.channelId}</p>
              <a href={c.channelUrl} target="_blank" rel="noopener noreferrer"
                 className="text-[11px] text-white/45 hover:text-[#c8a6ff] truncate inline-flex items-center gap-1">
                {c.channelUrl} <ExternalLink size={10} />
              </a>
            </div>
            {c.isPrimary && (
              <span className="text-[10px] font-bold uppercase text-[#8fb9de] shrink-0">Principal</span>
            )}
            {c.verified ? (
              <span className="inline-flex items-center gap-1 text-[11px] font-bold text-green-400 shrink-0">
                <ShieldCheck size={13} /> Verificado
              </span>
            ) : (
              <span className="text-[11px] text-white/40 shrink-0">Pendente</span>
            )}
            <button onClick={() => remove(c.id)} className="text-white/40 hover:text-red-400 cursor-pointer shrink-0">
              <Trash2 size={15} />
            </button>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
        <select value={platform} onChange={e => setPlatform(e.target.value)} className={inputCls}>
          {CONNECTION_PLATFORMS.map(p => <option key={p} value={p}>{PLATFORM_LABEL[p]}</option>)}
        </select>
        <input value={channelId} onChange={e => setChannelId(e.target.value)} placeholder="ID do canal" className={inputCls} />
        <input value={channelUrl} onChange={e => setChannelUrl(e.target.value)} placeholder="URL do canal" className={inputCls} />
        <button onClick={add} disabled={busy}
          className="px-4 py-2 rounded-md text-sm font-bold cursor-pointer disabled:opacity-50 flex items-center justify-center gap-1.5 transition"
          style={{ background: `${PURPLE}22`, color: '#e5d4ff', border: `1px solid ${PURPLE}` }}>
          {busy ? <Loader2 size={15} className="animate-spin" /> : <><Plus size={15} /> Conectar</>}
        </button>
      </div>
    </Panel>
  );
}

/* ── Redes sociais (link único, auto-detecta plataforma) ── */
function SocialsCard({ initial, notify }: {
  initial: StreamerSocial[]; notify: (t: any, m: string) => void;
}) {
  const [socials, setSocials] = useState<StreamerSocial[]>(initial);
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const detected = url.trim() ? detectPlatform(url.trim()) : '';

  const refresh = () => getSocials().then(setSocials).catch(() => {});

  const add = async () => {
    const v = url.trim();
    if (!v) { notify('error', 'Cole o link da sua rede.'); return; }
    setBusy(true);
    try {
      await addSocial(v, detectPlatform(v));
      notify('success', 'Rede social adicionada!');
      setUrl('');
      refresh();
    } catch (e: any) {
      notify('error', e?.message || 'Erro ao adicionar rede');
    } finally { setBusy(false); }
  };

  const remove = async (id: number) => {
    try { await deleteSocial(id); refresh(); notify('success', 'Rede removida.'); }
    catch (e: any) { notify('error', e?.message || 'Erro ao remover'); }
  };

  return (
    <Panel className="p-6">
      <h3 className="text-sm font-black uppercase tracking-wider text-[#c8a6ff] mb-1 flex items-center gap-2">
        <Link2 size={16} /> Minhas Redes Sociais
      </h3>
      <p className="text-[12px] text-white/50 mb-4">
        Cole o link — a plataforma é detectada automaticamente pelo domínio
        (Instagram, TikTok, X, Discord, Twitch, Facebook, YouTube, Kick).
      </p>

      <div className="flex flex-wrap gap-2 mb-4">
        {socials.length === 0 && (
          <p className="text-sm text-white/40 py-2">Nenhuma rede social cadastrada.</p>
        )}
        {socials.map(s => (
          <div key={s.id} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-white/[0.03] border border-[#9146FF]/20">
            <span className="text-[11px] font-black uppercase text-[#c8a6ff]">
              {PLATFORM_LABEL[s.platform] ?? s.platform}
            </span>
            <a href={s.url} target="_blank" rel="noopener noreferrer"
               className="text-[12px] text-white/70 hover:text-white truncate max-w-[180px] inline-flex items-center gap-1">
              {s.url} <ExternalLink size={10} />
            </a>
            <button onClick={() => remove(s.id)} className="text-white/40 hover:text-red-400 cursor-pointer">
              <Trash2 size={13} />
            </button>
          </div>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <div className="flex-1 relative">
          <input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://instagram.com/seucanal"
            className={inputCls} onKeyDown={e => { if (e.key === 'Enter') add(); }} />
          {detected && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-black uppercase text-[#c8a6ff]">
              {PLATFORM_LABEL[detected] ?? detected}
            </span>
          )}
        </div>
        <button onClick={add} disabled={busy}
          className="px-4 py-2 rounded-md text-sm font-bold cursor-pointer disabled:opacity-50 flex items-center justify-center gap-1.5 transition"
          style={{ background: `${PURPLE}22`, color: '#e5d4ff', border: `1px solid ${PURPLE}` }}>
          {busy ? <Loader2 size={15} className="animate-spin" /> : <><Plus size={15} /> Adicionar</>}
        </button>
      </div>
    </Panel>
  );
}

/* ═══════════════════════════ Cupons ═══════════════════════════ */

function daysRemaining(expiresAt?: string | null): { days: number; pct: number } {
  if (!expiresAt) return { days: 0, pct: 0 };
  const end = new Date(expiresAt).getTime();
  const now = Date.now();
  const msLeft = end - now;
  const days = Math.max(0, Math.ceil(msLeft / (1000 * 60 * 60 * 24)));
  // barra: assume janela de 5 dias (cupom fixo)
  const total = 5 * 24 * 60 * 60 * 1000;
  const pct = Math.max(0, Math.min(100, (msLeft / total) * 100));
  return { days, pct };
}

function CouponsTab({ me, onChanged, notify }: {
  me: StreamerMe; onChanged: () => void; notify: (t: any, m: string) => void;
}) {
  const [active, setActive] = useState<StreamerCoupon | null>(me.activeCoupon);
  const [history, setHistory] = useState<StreamerCoupon[]>([]);
  const [copied, setCopied] = useState(false);
  const [gen, setGen] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    getMyCoupons().then(v => setActive(v.activeCoupon)).catch(() => {});
    getFixedHistory().then(setHistory).catch(() => setHistory([]));
  }, []);

  const copy = async () => {
    if (!active) return;
    try {
      await navigator.clipboard.writeText(active.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { notify('error', 'Não foi possível copiar'); }
  };

  const generate = async () => {
    setGen(true);
    try {
      const c = await generateFixedCoupon();
      setActive(c);
      notify('success', 'Cupom fixo gerado!');
      getFixedHistory().then(setHistory).catch(() => {});
      onChanged();
    } catch (e: any) {
      notify('error', e?.message || 'Já existe um cupom ativo ou erro ao gerar.');
    } finally { setGen(false); }
  };

  // Renderiza o cartão do cupom num canvas e baixa como PNG.
  const downloadCard = () => {
    if (!active) return;
    const canvas = canvasRef.current ?? document.createElement('canvas');
    const W = 1000, H = 560;
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // fundo gradiente
    const g = ctx.createLinearGradient(0, 0, W, H);
    g.addColorStop(0, '#12071f');
    g.addColorStop(1, '#050208');
    ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);

    // brilho roxo
    const rg = ctx.createRadialGradient(W * 0.8, 90, 40, W * 0.8, 90, 480);
    rg.addColorStop(0, 'rgba(145,70,255,0.35)');
    rg.addColorStop(1, 'rgba(145,70,255,0)');
    ctx.fillStyle = rg; ctx.fillRect(0, 0, W, H);

    // borda neon
    ctx.strokeStyle = PURPLE; ctx.lineWidth = 4;
    ctx.strokeRect(20, 20, W - 40, H - 40);

    // header
    ctx.fillStyle = '#c8a6ff';
    ctx.font = '700 30px system-ui, sans-serif';
    ctx.fillText('CUPOM DE STREAMER · LIBERTY', 60, 100);

    ctx.fillStyle = '#ffffff';
    ctx.font = '800 44px system-ui, sans-serif';
    ctx.fillText(me.nick, 60, 160);

    // código grande
    ctx.fillStyle = '#ffffff';
    ctx.font = '900 120px system-ui, sans-serif';
    ctx.fillText(active.code, 60, 320);

    // cashback
    ctx.fillStyle = '#8fb9de';
    ctx.font = '800 40px system-ui, sans-serif';
    ctx.fillText(`+${active.cashbackPct}% de cashback na recarga`, 60, 400);

    ctx.fillStyle = 'rgba(255,255,255,0.6)';
    ctx.font = '500 26px system-ui, sans-serif';
    ctx.fillText(`Válido até ${formatDate(active.expiresAt)}`, 60, 450);

    ctx.fillStyle = 'rgba(255,255,255,0.4)';
    ctx.font = '500 22px system-ui, sans-serif';
    ctx.fillText('Use este código na tela de Recarga para apoiar o criador.', 60, 500);

    const link = document.createElement('a');
    link.download = `cupom-${active.code}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const { days, pct } = daysRemaining(active?.expiresAt);

  return (
    <div className="space-y-6">
      {/* Cartão do cupom ativo */}
      {active ? (
        <div className="relative overflow-hidden rounded-md p-8"
             style={{
               background: 'linear-gradient(135deg, #1a0b30 0%, #06030b 70%)',
               border: `1px solid ${PURPLE}`,
               boxShadow: `0 0 34px rgba(145,70,255,0.3)`,
             }}>
          <div className="absolute -top-20 -right-16 w-72 h-72 rounded-full pointer-events-none"
               style={{ background: 'radial-gradient(circle, rgba(145,70,255,0.35), transparent 70%)' }} />
          <div className="relative">
            <p className="text-[11px] font-black uppercase tracking-[0.3em] text-[#c8a6ff]">Seu cupom fixo</p>
            <p className="text-5xl md:text-6xl font-black text-white tracking-wider my-3"
               style={{ textShadow: `0 0 24px rgba(145,70,255,0.6)` }}>
              {active.code}
            </p>
            <p className="text-sm font-bold text-[#8fb9de]">
              +{active.cashbackPct}% de cashback · válido até {formatDate(active.expiresAt)}
            </p>

            {/* Barra de dias restantes */}
            <div className="mt-5 max-w-md">
              <div className="flex justify-between text-[11px] text-white/55 mb-1">
                <span>{days} {days === 1 ? 'dia restante' : 'dias restantes'}</span>
                <span>{Math.round(pct)}%</span>
              </div>
              <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full rounded-full transition-all"
                     style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${PURPLE}, #8fb9de)` }} />
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mt-6">
              <button onClick={copy}
                className="px-4 py-2 rounded-md text-sm font-bold cursor-pointer inline-flex items-center gap-2 transition"
                style={{ background: PURPLE, color: '#fff', boxShadow: `0 0 16px rgba(145,70,255,0.4)` }}>
                {copied ? <Check size={15} /> : <Copy size={15} />} {copied ? 'Copiado!' : 'Copiar Código'}
              </button>
              <button onClick={downloadCard}
                className="px-4 py-2 rounded-md text-sm font-bold cursor-pointer inline-flex items-center gap-2 transition"
                style={{ background: 'rgba(79,120,168,0.12)', color: '#8fb9de', border: '1px solid rgba(79,120,168,0.4)' }}>
                <Download size={15} /> Baixar Cartão (PNG)
              </button>
            </div>
          </div>
        </div>
      ) : (
        <Panel className="p-8 text-center" glow>
          <Ticket size={30} className="mx-auto text-[#c8a6ff] mb-3" />
          <h3 className="text-lg font-black text-white">Nenhum cupom fixo ativo</h3>
          <p className="text-sm text-white/55 mt-1 mb-5">Gere um cupom fixo de 5% válido por 5 dias.</p>
          <button onClick={generate} disabled={gen}
            className="px-6 py-2.5 rounded-md text-sm font-bold cursor-pointer disabled:opacity-50 inline-flex items-center gap-2 transition"
            style={{ background: PURPLE, color: '#fff', boxShadow: `0 0 18px rgba(145,70,255,0.4)` }}>
            {gen ? <Loader2 size={15} className="animate-spin" /> : <Ticket size={15} />} Gerar Cupom Fixo
          </button>
        </Panel>
      )}

      {/* Botão gerar (quando já existe ativo, fica desabilitado) */}
      {active && (
        <div>
          <button onClick={generate} disabled
            className="px-5 py-2 rounded-md text-sm font-bold opacity-50 cursor-not-allowed inline-flex items-center gap-2"
            style={{ background: `${PURPLE}22`, color: '#e5d4ff', border: `1px solid ${PURPLE}` }}>
            <Ticket size={15} /> Gerar Cupom Fixo
          </button>
          <span className="text-[12px] text-white/45 ml-3">Você já tem um cupom fixo ativo.</span>
        </div>
      )}

      {/* Regras do cupom Live progressivo */}
      <Panel className="p-6">
        <h3 className="text-sm font-black uppercase tracking-wider text-[#c8a6ff] mb-3 flex items-center gap-2">
          <Star size={16} /> Cupom Live — tiers progressivos (10% a 25%)
        </h3>
        <p className="text-[13px] text-white/60 mb-4">
          Durante uma sessão live verificada, seu cupom sobe de nível conforme o engajamento.
          Quanto mais espectadores e tempo ao vivo, maior o cashback que você oferece.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { tier: 'Bronze', pct: 10, c: '#cd7f32' },
            { tier: 'Prata', pct: 15, c: '#c0c0c0' },
            { tier: 'Ouro', pct: 20, c: '#FFD700' },
            { tier: 'Diamante', pct: 25, c: '#8fb9de' },
          ].map(t => (
            <div key={t.tier} className="rounded-md p-4 text-center"
                 style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${t.c}44` }}>
              <p className="text-[11px] font-black uppercase tracking-wide" style={{ color: t.c }}>{t.tier}</p>
              <p className="text-3xl font-black text-white mt-1">{t.pct}%</p>
            </div>
          ))}
        </div>
      </Panel>

      {/* Histórico de cupons fixos */}
      <Panel className="p-0 overflow-hidden">
        <h3 className="text-sm font-black uppercase tracking-wider text-[#c8a6ff] p-6 pb-3 flex items-center gap-2">
          <Clock size={16} /> Histórico de cupons fixos
        </h3>
        {history.length === 0 ? (
          <p className="text-sm text-white/40 px-6 pb-6">Nenhum cupom fixo anterior.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-y border-white/10 text-left">
                  <Th>Código</Th><Th>Cashback</Th><Th>Expira</Th>
                </tr>
              </thead>
              <tbody>
                {history.map(c => (
                  <tr key={c.id} className="border-b border-white/5">
                    <td className="px-6 py-3 font-mono font-bold text-white">{c.code}</td>
                    <td className="px-6 py-3 text-[#8fb9de] font-bold">+{c.cashbackPct}%</td>
                    <td className="px-6 py-3 text-white/60">{formatDate(c.expiresAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="px-6 py-3 text-[11px] font-black uppercase tracking-wider text-[#c8a6ff]">{children}</th>
  );
}

/* ═══════════════════════════ Comissões ═══════════════════════════ */

function CommissionsTab({ commissionPct }: { commissionPct: number }) {
  const [data, setData] = useState<CommissionsView | null>(null);
  const [status, setStatus] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getMyCommissions(status || undefined)
      .then(setData)
      .catch(() => setData({ items: [], totals: {} }))
      .finally(() => setLoading(false));
  }, [status]);

  const totals = data?.totals ?? {};

  return (
    <div className="space-y-6">
      <Panel className="p-5">
        <p className="text-[13px] text-white/70">
          Você recebe <span className="text-[#c8a6ff] font-black">{commissionPct || 5}%</span> de comissão
          sobre as recargas feitas com o seu cupom. As comissões são consolidadas e pagas conforme as regras do programa.
        </p>
      </Panel>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <MetricCard icon={<Wallet size={18} />} label="Total recargas" value={formatBRL(totals.brl)} accent="#8fb9de" />
        <MetricCard icon={<DollarSign size={18} />} label="Comissão total" value={formatBRL(totals.commissionBrl)} accent={PURPLE} />
        <MetricCard icon={<Clock size={18} />} label="Pendente" value={formatBRL(totals.pendingBrl)} accent="#fbbf24" />
        <MetricCard icon={<CheckCircle2 size={18} />} label="Pago" value={formatBRL(totals.paidBrl)} accent="#22c55e" />
      </div>

      <div className="flex gap-2">
        {[
          { v: '', l: 'Todas' },
          { v: 'pending', l: 'Pendentes' },
          { v: 'paid', l: 'Pagas' },
        ].map(f => (
          <button key={f.v} onClick={() => setStatus(f.v)}
            className="px-3 py-1.5 rounded-md text-xs font-bold cursor-pointer transition"
            style={status === f.v
              ? { background: `${PURPLE}22`, color: '#e5d4ff', border: `1px solid ${PURPLE}` }
              : { background: 'rgba(255,255,255,0.03)', color: 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.07)' }}>
            {f.l}
          </button>
        ))}
      </div>

      <Panel className="p-0 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 size={24} className="animate-spin text-[#c8a6ff]" /></div>
        ) : !data || data.items.length === 0 ? (
          <p className="text-sm text-white/40 text-center py-10">Nenhuma comissão registrada.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 text-left">
                  <Th>Data</Th><Th>Recarga</Th><Th>Comissão</Th><Th>Status</Th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((it, i) => (
                  <tr key={i} className="border-b border-white/5 hover:bg-white/[0.03]">
                    <td className="px-6 py-3 text-white/60">{formatDateTime(it.createdAt)}</td>
                    <td className="px-6 py-3 text-white/85">{formatBRL(it.brl)}</td>
                    <td className="px-6 py-3 text-[#c8a6ff] font-bold">{formatBRL(it.commissionBrl)}</td>
                    <td className="px-6 py-3">
                      <span className="text-[11px] font-bold uppercase px-2 py-1 rounded"
                            style={it.commissionStatus === 'paid'
                              ? { background: 'rgba(34,197,94,0.12)', color: '#4ade80' }
                              : { background: 'rgba(251,191,36,0.12)', color: '#fbbf24' }}>
                        {it.commissionStatus === 'paid' ? 'Paga' : 'Pendente'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}

/* ═══════════════════════════ Sessões Live ═══════════════════════════ */

function useLiveTimer(startAt?: string | null) {
  const [elapsed, setElapsed] = useState('00:00:00');
  useEffect(() => {
    if (!startAt) { setElapsed('00:00:00'); return; }
    const start = new Date(startAt).getTime();
    const tick = () => {
      const s = Math.max(0, Math.floor((Date.now() - start) / 1000));
      const h = String(Math.floor(s / 3600)).padStart(2, '0');
      const m = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
      const sec = String(s % 60).padStart(2, '0');
      setElapsed(`${h}:${m}:${sec}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [startAt]);
  return elapsed;
}

function SessionsTab({ me, onChanged, notify }: {
  me: StreamerMe; onChanged: () => void; notify: (t: any, m: string) => void;
}) {
  const [sessions, setSessions] = useState<StreamerSession[]>([]);
  const [platform, setPlatform] = useState<string>(me.connections.find(c => c.isPrimary)?.platform || 'twitch');
  const [busy, setBusy] = useState(false);
  const active = me.activeSession;
  const elapsed = useLiveTimer(active?.startAt);

  useEffect(() => { getMySessions().then(setSessions).catch(() => setSessions([])); }, []);

  const start = async () => {
    setBusy(true);
    try {
      await startLive(platform);
      notify('success', 'Sessão iniciada! A verificação ocorre no servidor.');
      onChanged();
      getMySessions().then(setSessions).catch(() => {});
    } catch (e: any) {
      notify('error', e?.message || 'Erro ao iniciar live');
    } finally { setBusy(false); }
  };

  const stop = async () => {
    setBusy(true);
    try {
      await stopLive();
      notify('success', 'Sessão encerrada.');
      onChanged();
      getMySessions().then(setSessions).catch(() => {});
    } catch (e: any) {
      notify('error', e?.message || 'Erro ao encerrar live');
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      {/* Controle da sessão */}
      <div className="relative overflow-hidden rounded-md p-6"
           style={{
             background: active
               ? 'linear-gradient(135deg, #2a0812 0%, #06030b 70%)'
               : 'linear-gradient(180deg, #0d0714 0%, #060309 100%)',
             border: active ? '1px solid rgba(239,68,68,0.55)' : `1px solid ${PURPLE}33`,
             boxShadow: active ? '0 0 30px rgba(239,68,68,0.22)' : `0 0 20px rgba(145,70,255,0.12)`,
           }}>
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-1">
            {active ? (
              <>
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                  <span className="text-[11px] font-black uppercase tracking-widest text-red-400">Ao vivo agora</span>
                </div>
                <p className="text-4xl font-black text-white font-mono tabular-nums" style={{ textShadow: '0 0 18px rgba(239,68,68,0.4)' }}>
                  {elapsed}
                </p>
                <p className="text-[12px] text-white/55 mt-1">
                  Plataforma: <span className="text-white font-bold uppercase">{active.platform}</span> · início {formatDateTime(active.startAt)}
                </p>
              </>
            ) : (
              <>
                <p className="text-[11px] font-black uppercase tracking-widest text-[#c8a6ff] mb-1">Fora do ar</p>
                <p className="text-lg font-bold text-white">Inicie uma sessão para ativar seu cupom live.</p>
                <div className="mt-3 w-full sm:w-52">
                  <select value={platform} onChange={e => setPlatform(e.target.value)} className={inputCls}>
                    {CONNECTION_PLATFORMS.map(p => <option key={p} value={p}>{PLATFORM_LABEL[p]}</option>)}
                  </select>
                </div>
              </>
            )}
          </div>
          <div className="shrink-0">
            {active ? (
              <button onClick={stop} disabled={busy}
                className="px-6 py-3 rounded-md text-sm font-black uppercase tracking-wide cursor-pointer disabled:opacity-50 inline-flex items-center gap-2 transition"
                style={{ background: 'rgba(239,68,68,0.9)', color: '#fff', boxShadow: '0 0 18px rgba(239,68,68,0.5)' }}>
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Square size={16} />} Encerrar Live
              </button>
            ) : (
              <button onClick={start} disabled={busy}
                className="px-6 py-3 rounded-md text-sm font-black uppercase tracking-wide cursor-pointer disabled:opacity-50 inline-flex items-center gap-2 transition"
                style={{ background: PURPLE, color: '#fff', boxShadow: '0 0 18px rgba(145,70,255,0.5)' }}>
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />} Iniciar Live
              </button>
            )}
          </div>
        </div>
        <p className="text-[11px] text-white/40 mt-4 flex items-center gap-1.5">
          <ShieldCheck size={12} /> A regra de 30 min + verificação de "está ao vivo" é validada no servidor via API — não há como burlar pelo cliente.
        </p>
      </div>

      {/* Histórico de sessões */}
      <Panel className="p-0 overflow-hidden">
        <h3 className="text-sm font-black uppercase tracking-wider text-[#c8a6ff] p-6 pb-3 flex items-center gap-2">
          <Clock size={16} /> Histórico de sessões
        </h3>
        {sessions.length === 0 ? (
          <p className="text-sm text-white/40 px-6 pb-6">Nenhuma sessão registrada ainda.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-y border-white/10 text-left">
                  <Th>Plataforma</Th><Th>Início</Th><Th>Fim</Th><Th>Duração</Th><Th>Validada</Th>
                </tr>
              </thead>
              <tbody>
                {sessions.map(s => (
                  <tr key={s.id} className="border-b border-white/5 hover:bg-white/[0.03]">
                    <td className="px-6 py-3 text-white uppercase font-bold">{s.platform}</td>
                    <td className="px-6 py-3 text-white/60">{formatDateTime(s.startAt)}</td>
                    <td className="px-6 py-3 text-white/60">{s.endAt ? formatDateTime(s.endAt) : '—'}</td>
                    <td className="px-6 py-3 text-white/85">{s.durationMin != null ? `${s.durationMin} min` : '—'}</td>
                    <td className="px-6 py-3">
                      {s.verified ? (
                        <span className="inline-flex items-center gap-1 text-green-400 text-xs font-bold">
                          <ShieldCheck size={13} /> Sim
                        </span>
                      ) : (
                        <span className="text-white/40 text-xs">Não</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}

/* ═══════════════════════════ Como Apareço ═══════════════════════════ */

function AppearanceTab({ me }: { me: StreamerMe }) {
  const primary = me.connections.find(c => c.isPrimary && c.verified)
    ?? me.connections.find(c => c.verified)
    ?? me.connections[0];
  const isLive = !!me.activeSession;
  const embedUrl = primary && isLive ? buildEmbedUrl(primary) : null;

  return (
    <div className="space-y-6">
      <Panel className="p-6">
        <h3 className="text-sm font-black uppercase tracking-wider text-[#c8a6ff] mb-4 flex items-center gap-2">
          <Eye size={16} /> Prévia do seu card público
        </h3>
        <p className="text-[13px] text-white/55 mb-5">
          É assim que você aparece na página pública de streamers da Liberty
          (<span className="text-white/80 font-mono">/streamers</span>).
        </p>

        {/* Card público simulado */}
        <div className="max-w-xs">
          <div className="rounded-md overflow-hidden"
               style={{ background: 'linear-gradient(180deg, #0d0714, #060309)', border: `1px solid ${PURPLE}44`, boxShadow: `0 0 22px rgba(145,70,255,0.18)` }}>
            <div className="relative h-24"
                 style={{ background: `radial-gradient(ellipse at top, ${PURPLE}44, transparent 70%)` }}>
              {isLive && (
                <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-black uppercase text-white"
                      style={{ background: 'rgba(239,68,68,0.9)' }}>
                  <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> LIVE
                </span>
              )}
              <div className="absolute -bottom-6 left-4 w-14 h-14 rounded-md overflow-hidden border-2"
                   style={{ borderColor: `${PURPLE}`, boxShadow: `0 0 12px rgba(145,70,255,0.5)` }}>
                {me.avatar
                  ? <img src={me.avatar} alt={me.nick} className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center bg-black/60"><Tv size={20} className="text-[#c8a6ff]" /></div>}
              </div>
            </div>
            <div className="pt-8 px-4 pb-4">
              <p className="text-white font-black truncate">{me.nick}</p>
              <p className="text-[11px] text-white/45 uppercase font-bold">
                {primary ? (PLATFORM_LABEL[primary.platform] ?? primary.platform) : 'Sem canal'}
              </p>
              {me.bio && <p className="text-[12px] text-white/60 mt-2 line-clamp-2">{me.bio}</p>}
              {primary && (
                <a href={primary.channelUrl} target="_blank" rel="noopener noreferrer"
                   className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-[#c8a6ff] hover:text-white">
                  Ver canal <ExternalLink size={12} />
                </a>
              )}
            </div>
          </div>
        </div>
      </Panel>

      {/* Embed da própria live */}
      <Panel className="p-6">
        <h3 className="text-sm font-black uppercase tracking-wider text-[#c8a6ff] mb-4 flex items-center gap-2">
          <Radio size={16} /> Sua transmissão ao vivo
        </h3>
        {embedUrl ? (
          <div className="rounded-md overflow-hidden border border-[#9146FF]/30" style={{ aspectRatio: '16 / 9' }}>
            <iframe
              src={embedUrl}
              title="Minha live"
              allowFullScreen
              className="w-full h-full"
              frameBorder="0"
              scrolling="no"
            />
          </div>
        ) : (
          <div className="rounded-md border border-white/10 bg-black/40 flex flex-col items-center justify-center text-center py-12">
            <Video size={30} className="text-white/30 mb-3" />
            <p className="text-sm text-white/50">
              {isLive
                ? 'Conecte e verifique um canal principal para exibir sua live aqui.'
                : 'Você aparecerá aqui quando estiver ao vivo com um canal principal verificado.'}
            </p>
          </div>
        )}
      </Panel>
    </div>
  );
}
